import { useCallback, useEffect, useRef, useState } from "react";

import { getInitialDragOffset, type AspectRatio, type DragOffset } from "./helpers";

import { detectImageAspectRatio, fitImageToSquare, type FitImageResult } from "@/components/ImageUploader/utils";

type UseSquareImagePreparationParams = {
  imageDataUrl: string;
  targetSize: number;
};

export const useSquareImagePreparation = ({ imageDataUrl, targetSize }: UseSquareImagePreparationParams) => {
  const [aspectRatio, setAspectRatio] = useState<AspectRatio | null>(null);
  const [fittedImage, setFittedImage] = useState<FitImageResult | null>(null);
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const lastImageDataUrlRef = useRef<string | null>(null);

  const initialize = useCallback(async () => {
    if (!imageDataUrl || lastImageDataUrlRef.current === imageDataUrl) {
      return;
    }

    setIsLoading(true);

    const detectedRatio = await detectImageAspectRatio(imageDataUrl);
    setAspectRatio(detectedRatio);

    const fitted = await fitImageToSquare(imageDataUrl, targetSize);
    const initialDragOffset = getInitialDragOffset(detectedRatio, fitted, targetSize);
    setFittedImage(fitted);
    setDragOffset(initialDragOffset);

    lastImageDataUrlRef.current = imageDataUrl;
    setIsLoading(false);
  }, [imageDataUrl, targetSize]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  return {
    aspectRatio,
    dragOffset,
    fittedImage,
    isLoading,
    setDragOffset,
  };
};
